import { createAction, props } from '@ngrx/store';
import { Shipment } from '../../models/shipment';
import { CourierOptionDefinition } from '../../models/courier-option-definition';
import { ReferencesModification } from '../../models/references-modification';
import { Violation } from '../../models/violation';
import { Card } from '../../models/card';
import { Package } from '../../models/package';
import { PaymentOptionEnum } from '../../enum/payment-option.enum';
import { SignatureOptionEnum } from '../../enum/signature-option.enum';
import { Commodity } from '../../models/commodity';
import { PackagingTypeEnum } from '../../enum/packaging-type.enum';
import { ValidationMode } from '../../enum/validation-mode.enum';
import { Customs } from '../../models/customs';

export const shipmentEditorLoadRequest = createAction(
    '[Shipment Editor] Load Shipment Request',
    props<{ id: string }>()
);

export const shipmentEditorLoadSuccess = createAction(
    '[Shipment Editor] Load Shipment Success',
    props<{ shipment: Shipment }>()
);

export const shipmentEditorLoadFailure = createAction(
    '[Shipment Editor] Load Shipment Failure'
);

export const shipmentEditorSaveRequest = createAction(
    '[Shipment Editor] Save Shipment Request'
);

export const shipmentEditorSaveSuccess = createAction(
    '[Shipment Editor] Editor Data Synced'
);

export const shipmentEditorSaveFailure = createAction(
    '[Shipment Editor] Save Shipment Failure'
);

export const shipmentEditorAddCourierOption = createAction(
    '[Shipment Editor] Add Courier Option',
    props<{ option: CourierOptionDefinition }>()
);

export const shipmentEditorRemoveCourierOption = createAction(
    '[Shipment Editor] Remove Courier Option',
    props<{ option: CourierOptionDefinition }>()
);

export const shipmentEditorCreateReference = createAction(
    '[Shipment Editor] Create Reference from Definition',
    props<{ modification: ReferencesModification }>()
);

export const shipmentEditorAddReference = createAction(
    '[Shipment Editor] Add Reference'
);

export const shipmentEditorRemoveReference = createAction(
    '[Shipment Editor] Remove Reference',
    props<{ index: number }>()
);

export const shipmentEditorUpdate = createAction(
    '[Shipment Editor] Update State',
    props<{ shipment: Shipment }>()
);

export const shipmentEditorRecordUndo = createAction(
    '[Shipment Editor] Record Undo State'
);

export const shipmentEditorUndo = createAction(
    '[Shipment Editor] Pull shipment from undo stack'
);

export const shipmentEditorRedo = createAction(
    '[Shipment Editor] Pull shipment from redo stack'
);

export const shipmentEditorInvalidate = createAction(
    '[Shipment Editor] Invalid shipment ID on leaving editor'
);

export const shipmentEditorSetMode = createAction(
    '[Shipment Editor] Set Mode',
    props<{ quickQuote: boolean }>()
);

export const shipmentEditorSetViolations = createAction(
    '[Shipment Editor] Set Violations',
    props<{ violations: Violation[] }>()
);

export const shipmentEditorDisplayQuotes = createAction(
    '[Shipment Editor] Display Quotes'
);

export const shipmentEditorDisplayEditor = createAction(
    '[Shipment Editor] Display Editor'
);

export const shipmentEditorSetActiveOptions = createAction(
    '[Shipment Editor] Set Active Options',
    props<{ options: string[] }>()
);

export const shipmentEditorSetOption = createAction(
    '[Shipment Editor] Set Option',
    props<{ option: string, value: boolean | string }>()
);

export const shipmentEditorSetOptionData = createAction(
    '[Shipment Editor] Set Option Data',
    props<{ courierOptions: {} }>()
);

export const shipmentEditorChangePreSelectedQuote = createAction(
    '[Shipment Table] Change Pre-selected Quote',
    props<{ shipmentId: string, quoteId: number }>()
);

export const shipmentEditorUpdateShipper = createAction(
    '[Shipment Editor] Update Shipper',
    props<{ card: Partial<Card> }>()
);

export const shipmentEditorUpdateDestination = createAction(
    '[Shipment Editor] Update Destination',
    props<{ card: Partial<Card> }>()
);

export const shipmentEditorUpdateBroker = createAction(
    '[Shipment Editor] Update Broker',
    props<{ card: Partial<Card> }>()
);

export const shipmentEditorUpdateSoldTo = createAction(
    '[Shipment Editor] Update Sold To',
    props<{ card: Partial<Card> }>()
);

export const shipmentEditorUpdatePackageSection = createAction(
    '[Shipment Editor] Update Packages Section',
    props<{ documentsOnly: boolean, packagingType: PackagingTypeEnum, weightUnit: string, currency: string, packages: Package[] }>()
);

export const shipmentEditorUpdateOptionsSection = createAction(
    '[Shipment Editor] Update Options Section',
    props<{ shipmentDate: string, paymentOption: PaymentOptionEnum, paymentAccountNumber: string, signatureOption: string }>()
);

export const shipmentEditorAddPackage = createAction(
    '[Shipment Editor] Add Package'
);

export const shipmentEditorAddEmptyPackage = createAction(
    '[Shipment Editor] Add Empty Package'
);

export const shipmentEditorAddPackageFromTemplate = createAction(
    '[Shipment Editor] Add Package from Template',
    props<{ template: Package }>()
);

export const shipmentEditorDeletePackage = createAction(
    '[Shipment Editor] Delete Package',
    props<{ packageIndex: number }>()
);

export const shipmentEditorDeletePackages = createAction(
    '[Shipment Editor] Delete Packages',
    props<{ packageIndices: number[] }>()
);

export const shipmentEditorAddCommodity = createAction(
    '[Shipment Editor] Add Commodity',
    props<{ packageIndex: number }>()
);

export const shipmentEditorAddEmptyCommodity = createAction(
    '[Shipment Editor] Add Empty Commodity',
    props<{ packageIndex: number }>()
);

export const shipmentEditorAddCommodityFromTemplate = createAction(
    '[Shipment Editor] Add Commodity from Library',
    props<{ packageIndex: number, template: Commodity }>()
);

export const shipmentEditorDeleteCommodity = createAction(
    '[Shipment Editor] Delete Commodity',
    props<{ packageIndex: number, commodityIndex: number }>()
);

export const shipmentEditorPatchOptions = createAction(
    '[Shipment Editor] Update Options',
    props<{ options: string[] }>()
);

export const shipmentEditorPatchReferences = createAction(
    '[Shipment Editor] Update References',
    props<{ references: string[] }>()
);

export const shipmentEditorPatchForms = createAction(
    '[Shipment Editor] Update Customs Forms',
    props<{ forms: string[] }>()
);

export const shipmentEditorPatchCustoms = createAction(
    '[Shipment Editor] Update Customs',
    props<{ customs: Customs }>()
)

export const shipmentEditorSetInsurance = createAction(
    '[Shipment Editor] Set Insurance State',
    props<{ insurance: boolean }>()
);

export const shipmentEditorSetPackagingType = createAction(
    '[Shipment Editor] Set Packaging Type',
    props<{ packagingType: PackagingTypeEnum }>()
);

export const shipmentEditorSetSignatureOption = createAction(
    '[Shipment Editor] Set Signature Option',
    props<{ signatureOption: SignatureOptionEnum }>()
);

export const shipmentEditorSetWeightUnit = createAction(
    '[Shipment Editor] Set Weight Unit',
    props<{ weightUnit: string }>()
);

export const shipmentEditorSetCurrency = createAction(
    '[Shipment Editor] Set Currency',
    props<{ currency: string }>()
);

export const shipmentEditorSetShipmentDate = createAction(
    '[Shipment Editor] Set Shipment Date',
    props<{ shipmentDate: string }>()
);

export const shipmentEditorChangeDetected = createAction(
    '[Shipment Editor] Change Detected'
);

export const shipmentEditorDeTemplatePackage = createAction(
    '[Shipment Editor] De-Template Package',
    props<{ packageIndex: number }>()
);

export const shipmentEditorDeTemplateCommodity = createAction(
    '[Shipment Editor] De-Template Commodity',
    props<{ packageIndex: number, commodityIndex: number}>()
);

export const shipmentEditorSetCategory = createAction(
    '[Shipment Editor] Set Category',
    props<{ category: string }>()
);

export const shipmentEditorPatchPackages = createAction(
    '[Shipment Editor] Patch Packages',
    props<{ indices: number[], pkg: Partial<Package> }>()
);

export const shipmentEditorMoveCommodities = createAction(
    '[Shipment Editor] Move Commodities',
    props<{ indices: number[], targetPackageIndex: number }>()
);

export const shipmentEditorPatchCommodities = createAction(
    '[Shipment Editor] Patch Commodities',
    props<{ indices: number[], com: Partial<Commodity> }>()
);

export const shipmentEditorDeleteCommodities = createAction(
    '[Shipment Editor] Delete Commodities',
    props<{ indices: number[] }>()
);

export const shipmentEditorSetValidationMode = createAction(
    '[Shipment Editor] Set Validation Mode',
    props<{ mode: ValidationMode }>()
);

export const shipmentEditorValidateQuotes = createAction(
    '[Shipment Editor] Change Validation Mode to include Quotes'
);

export const shipmentEditorGetQuotes = createAction(
    '[Shipment Editor] Get Quotes'
);

export const shipmentEditorSwapAddresses = createAction(
    '[Shipment Editor] Swap Shipper and Destination'
);

export const shipmentEditorOpenDocumentUploadModal = createAction(
    '[Shipment Editor] Open Document Upload Modal',
    props<{ file?: File }>()
);

export const shipmentEditorDocumentUploadSuccess = createAction(
    '[Shipment Editor] Document Upload Success'
);

export const shipmentEditorDeleteDocumentRequest = createAction(
    '[Shipment Editor] Delete Document Request',
    props<{ documentId: string }>()
);

export const shipmentEditorDeleteDocumentSuccess = createAction(
    '[Shipment Editor] Delete Document Success',
    props<{ documentId: string }>()
);

export const shipmentEditorDeleteDocumentFailure = createAction(
    '[Shipment Editor] Delete Document Failure',
    props<{ documentId: string }>()
);
